import { isEqual, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { OpenFieldSection, OpenSection } from '../../../../datasets/instances/store/types';
import { isFormDatasetDefinition, isGroupField } from '../../../../datasets/store/typeAssertions';
import { DatasetField, DatasetFieldType, DatasetSection, DatasetType, DefinitionHierarchy, DocumentSpecificHiddenFields, FormDatasetDefinition, HiddenFields, OpenFieldType, SingleDatasetField } from '../../../../datasets/store/types';
import { Icon } from '../../../../shared/icon/Icon';
import { CaretDown, CaretSide } from '../../../../shared/icons';
import { Scrollable } from '../../../../shared/scrollable/Scrollable';
import { InformationTooltip } from '../../../../shared/tooltip';
import { addDocumentReportField, addOpenFieldSection, DocumentReportField, getDefinitionHierarchy, getReportFields, getReportFieldsTableDatasetId, getReportMetaFields, getReportOpenFieldSections, getSelectedDocumentNameIds, removeDocumentReportField, removeOpenFieldSection } from '../../store';
import styles from '../ReportDefinitions.module.scss';
import { GroupField } from './fields/GroupField';
import { MultiToggleField } from './fields/MultiToggleField';
import { SingleField } from './fields/SingleField';
import { Toggle } from '../../../../shared/toggle';
import { getDocumentReportField, getFlattenedFields, getIsSelectedMetaField } from '../../store/utils';

const { green } = styles;

interface FormDefinitionProps {
    formDefinition: FormDatasetDefinition;
    parents: number;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    parentFieldId: string;
}

interface FieldsProps {
    fields: DatasetField[];
    sectionId: string;
    datasetOnlySection?: boolean;
    datasetId: number;
    parents: number;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    parentFieldId: string;
}

export const Fields: React.FC<FieldsProps> = ({
    fields,
    sectionId,
    datasetOnlySection = false,
    datasetId,
    parents,
    toggleSection,
    getSectionOpen,
    hiddenFields,
    datasetHidden,
    documentSpecificHiddenFields,
    parentHiddenDocumentNameIds,
    parentFieldId
}) => {
    const getPreviewContent = useCallback((field: DatasetField, index: number) => {
        const isLastFieldInSection = index === fields.length - 1;
        if (isGroupField(field)) {
            if (field.type === DatasetFieldType.GROUP) {
                return (
                    <GroupField
                        field={field}
                        key={field.id}
                        groupIndex={index}
                        sectionId={sectionId}
                        datasetId={datasetId}
                        parents={parents}
                        toggleSection={toggleSection}
                        getSectionOpen={getSectionOpen}
                        hiddenFields={hiddenFields}
                        datasetHidden={datasetHidden}
                        documentSpecificHiddenFields={documentSpecificHiddenFields}
                        parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                        isLastFieldInSection={isLastFieldInSection}
                        parentFieldId={parentFieldId}
                    />
                );
            }
            if (field.type === DatasetFieldType.MULTI_TOGGLE) {
                return (
                    <MultiToggleField
                        field={field}
                        key={field.id}
                        groupIndex={index}
                        sectionId={sectionId}
                        datasetId={datasetId}
                        hiddenFields={hiddenFields}
                        datasetHidden={datasetHidden}
                        documentSpecificHiddenFields={documentSpecificHiddenFields}
                        parentFieldId={parentFieldId}
                    />
                );
            }
        }
        return (
            <SingleField
                field={field as SingleDatasetField}
                key={field.id}
                sectionId={sectionId}
                datasetOnlySection={datasetOnlySection}
                datasetId={datasetId}
                parents={parents}
                toggleSection={toggleSection}
                getSectionOpen={getSectionOpen}
                hiddenFields={hiddenFields}
                datasetHidden={datasetHidden}
                documentSpecificHiddenFields={documentSpecificHiddenFields}
                parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                isLastFieldInSection={isLastFieldInSection}
                parentFieldId={parentFieldId}
            />
        );
    }, [
        sectionId,
        datasetOnlySection,
        datasetId,
        parents,
        toggleSection,
        getSectionOpen,
        hiddenFields,
        datasetHidden,
        documentSpecificHiddenFields,
        parentHiddenDocumentNameIds,
        fields.length,
        parentFieldId
    ]);

    return (
        <div className={styles.allFieldsWrapper}>
            {fields.map((field, index) => getPreviewContent(field, index))}
        </div>
    );
};

interface SectionProps {
    section: DatasetSection;
    formDefinition: FormDatasetDefinition;
    datasetId: number;
    parents: number;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    parentFieldId: string;
}

export const Section: React.FC<SectionProps> = ({
    section,
    formDefinition,
    datasetId,
    parents,
    toggleSection,
    getSectionOpen,
    hiddenFields,
    datasetHidden,
    documentSpecificHiddenFields,
    parentHiddenDocumentNameIds = [],
    parentFieldId
}) => {
    const dispatch = useAppDispatch();
    const reportDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);
    const selectedMetaFields = useAppSelector(getReportMetaFields);
    const reportFields = useAppSelector(getReportFields);
    const definitionHierarchy = useAppSelector(getDefinitionHierarchy);
    const includedTableDatasetId = useAppSelector(getReportFieldsTableDatasetId);

    const { id, label, description } = section;
    const openSection: OpenSection = { sectionId: id, datasetId: datasetId.toString(), type: OpenFieldType.SECTION, parentFieldId };
    const sectionOpen = getSectionOpen(openSection);
    const fields = formDefinition.datasetFields[id];
    const datasetOnlySection = useMemo(() => fields.length === 1 && fields[0].type === DatasetFieldType.DATASET, [fields]);
    const sectionOpenIcon = sectionOpen ? CaretDown : CaretSide;

    const getDefinitionInHierarchy = useCallback((datasetId: number, parentFieldId: string) => definitionHierarchy.find(({ fieldId, definition }) => isEqual({ parentFieldId: fieldId, datasetId: definition.datasetId! }, { parentFieldId, datasetId })) || null, [definitionHierarchy]);

    const definitionInHierarchy = useMemo(() => {
        if (!datasetOnlySection) {
            return getDefinitionInHierarchy(datasetId, parentFieldId);
        }
        let isDatasetOnlySection: boolean = datasetOnlySection;
        let childParentFieldId = fields[0].id!;
        let childDatasetId = parseInt((fields[0] as SingleDatasetField).settings.datasetLinked!);
        while (isDatasetOnlySection) {
            const childDefinition = getDefinitionInHierarchy(childDatasetId, childParentFieldId)?.definition;
            if (!isUndefined(childDefinition) && isFormDatasetDefinition(childDefinition) && childDefinition.datasetSections.length === 1) {
                const childFields = Object.values(childDefinition.datasetFields)[0];
                const isDatasetOnlyChild = childFields.length === 1 && childFields[0].type === DatasetFieldType.DATASET;
                isDatasetOnlySection = isDatasetOnlyChild;
                if (isDatasetOnlyChild) {
                    childParentFieldId = childFields[0].id!;
                    childDatasetId = parseInt((childFields[0] as SingleDatasetField).settings.datasetLinked!);
                }
            } else {
                isDatasetOnlySection = false;
            }
        }
        return getDefinitionInHierarchy(childDatasetId, childParentFieldId);
    }, [fields, datasetId, parentFieldId, datasetOnlySection, getDefinitionInHierarchy]);

    const notParentDatasetSection = useMemo(() => !isNull(definitionInHierarchy) && !definitionHierarchy.map(({ parentId }) => parentId).includes(definitionInHierarchy.definition.datasetId!), [definitionHierarchy, definitionInHierarchy]);
    const noChildDatasetsSection = useMemo(() => fields.length > 1 && fields.every(({ type }) => type !== DatasetFieldType.DATASET), [fields]);

    const flattenedFields = useMemo(() => {
        const sectionId = !datasetOnlySection ? id : null;
        if (!isNull(definitionInHierarchy)) {
            return getFlattenedFields(definitionInHierarchy.definition, sectionId);
        }
        return getFlattenedFields(formDefinition);
    }, [definitionInHierarchy, formDefinition, datasetOnlySection, id]);

    const currentHiddenDatasetFields = useMemo(() => hiddenFields[datasetId] || [], [datasetId, hiddenFields]);
    const sectionHidden = useMemo(() => !!currentHiddenDatasetFields.filter(({ type }) => type === OpenFieldType.SECTION).find(({ sectionId }) => sectionId === id), [currentHiddenDatasetFields, id]);

    const documentHiddenFields = useMemo(() => documentSpecificHiddenFields[datasetId] || [], [datasetId, documentSpecificHiddenFields]);
    const currentHiddenDocumentsForSection = useMemo(() => documentHiddenFields.filter(({ type }) => type === OpenFieldType.SECTION).find(({ sectionId }) => sectionId === id)?.documentNameIds || [], [documentHiddenFields, id]);
    const sectionHiddenForAllDatasetDocuments = useMemo(() => currentHiddenDocumentsForSection.length > 0 && reportDocumentNameIds.every(documentNameId => currentHiddenDocumentsForSection.includes(parseInt(documentNameId))), [reportDocumentNameIds, currentHiddenDocumentsForSection]);

    const checkIsSelectedMetaField = useCallback((field: SingleDatasetField) => field.type === DatasetFieldType.DOCUMENT && !isNull(field.settings.documentProperty) && !isUndefined(field.settings.documentProperty) && getIsSelectedMetaField(field.settings.documentProperty, selectedMetaFields), [selectedMetaFields]);
    const checkIsIncludedReportField = useCallback((documentReportField: DocumentReportField) => !isUndefined(reportFields.find(reportField => isEqual(documentReportField, reportField))), [reportFields]);

    const isIncludedField = useCallback((definitionInHierarchy: DefinitionHierarchy, field: SingleDatasetField) => {
        const isIncludedReportField = checkIsIncludedReportField(getDocumentReportField(definitionInHierarchy, field, id));
        const isSelectedMetaField = checkIsSelectedMetaField(field);
        return isIncludedReportField || isSelectedMetaField;
    }, [checkIsSelectedMetaField, checkIsIncludedReportField, id]);

    const allFieldsIncludedInReport = useMemo(() => !isNull(definitionInHierarchy) && flattenedFields.every(field => isIncludedField(definitionInHierarchy, field)), [definitionInHierarchy, flattenedFields, isIncludedField]);

    const addAllReportFields = useCallback(() => !isNull(definitionInHierarchy) && flattenedFields.filter(field => !isIncludedField(definitionInHierarchy, field)).forEach(field => {
        if (field.type !== DatasetFieldType.DOCUMENT) {
            const documentReportField = getDocumentReportField(definitionInHierarchy, field, id);
            dispatch(addDocumentReportField(documentReportField));
        }
    }), [dispatch, definitionInHierarchy, flattenedFields, id, isIncludedField]);

    const removeAllReportFields = useCallback(() => flattenedFields.forEach(field => {
        if (field.type !== DatasetFieldType.DOCUMENT) {
            dispatch(removeDocumentReportField(field.id!));
        }
    }), [dispatch, flattenedFields]);

    const toggleAllReportFields = useCallback((checked: boolean, e: MouseEvent | React.SyntheticEvent<MouseEvent | KeyboardEvent, Event>) => {
        e.stopPropagation();
        checked ? addAllReportFields() : removeAllReportFields();
    }, [removeAllReportFields, addAllReportFields]);

    const showToggle = useMemo(() => ((datasetOnlySection && notParentDatasetSection) || noChildDatasetsSection) && !flattenedFields.every(({ type }) => type === DatasetFieldType.DOCUMENT), [datasetOnlySection, notParentDatasetSection, noChildDatasetsSection, flattenedFields]);
    const selectAllToggleEnabled = useMemo(() => isNull(includedTableDatasetId) || (!isNull(definitionInHierarchy) && (definitionInHierarchy.definition.datasetType === DatasetType.FORM || definitionInHierarchy.definition.datasetId! === includedTableDatasetId)), [includedTableDatasetId, definitionInHierarchy]);

    if (sectionHidden || sectionHiddenForAllDatasetDocuments) {
        return null;
    }

    return (
        <div key={id} className={styles.sectionWrapper} data-testid={`form-section-${id}-wrapper`} id={`document-report-section-${id}`}>
            <div className={styles.sectionHeaderWrapper}>
                <div className={styles.sectionHeader} onClick={() => toggleSection(openSection)} data-testid={`form-section-${id}-collapse`}>
                    <div className={styles.sectionOpenIcon}>
                        <Icon icon={sectionOpenIcon} fontSize={15} />
                    </div>
                    <div className={styles.sectionLabel} data-testid={`form-section-${id}-label`}>{label}</div>
                    {description && <div className={styles.sectionDescription}><InformationTooltip content={description} /></div>}
                    {showToggle && <Toggle checked={allFieldsIncludedInReport} onChange={toggleAllReportFields} disabled={!selectAllToggleEnabled} onColour={green} />}
                </div>
            </div>
            {sectionOpen &&
                <Fields
                    fields={fields}
                    sectionId={id}
                    datasetOnlySection={datasetOnlySection}
                    datasetId={datasetId}
                    parents={parents}
                    toggleSection={toggleSection}
                    getSectionOpen={getSectionOpen}
                    hiddenFields={hiddenFields}
                    datasetHidden={datasetHidden}
                    documentSpecificHiddenFields={documentSpecificHiddenFields}
                    parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                    parentFieldId={parentFieldId}
                />
            }
        </div>
    );
};

export const FormDefinition: React.FC<FormDefinitionProps> = ({ formDefinition, parents, hiddenFields, datasetHidden, documentSpecificHiddenFields, parentHiddenDocumentNameIds, parentFieldId }) => {
    const dispatch = useAppDispatch();
    const { datasetSections, datasetFields, datasetId } = formDefinition;
    const firstSection = useMemo(() => datasetSections[0], [datasetSections]);
    const isShortFormInstance = useMemo(() => datasetSections.length === 1 && !firstSection.label, [datasetSections, firstSection]);
    const shortFormFields = useMemo(() => datasetFields[firstSection.id], [datasetFields, firstSection]);
    const datasetOnlySection = useMemo(() => shortFormFields.length === 1 && shortFormFields[0].type === DatasetFieldType.DATASET, [shortFormFields]);
    const openFieldsAndSections = useAppSelector(getReportOpenFieldSections);
    const getSectionOpen = useCallback((fieldSection: OpenFieldSection) => openFieldsAndSections.some(openSection => isEqual(openSection, fieldSection)), [openFieldsAndSections]);

    const toggleSection = useCallback((fieldSection: OpenFieldSection) => {
        const sectionOpen = openFieldsAndSections.find(openSection => isEqual(openSection, fieldSection));
        sectionOpen ? dispatch(removeOpenFieldSection(sectionOpen)) : dispatch(addOpenFieldSection(fieldSection));
    }, [dispatch, openFieldsAndSections]);

    return (
        <div className={styles.formDefinitionWrapper} data-testid='form-definition-wrapper'>
            <Scrollable>
                {isShortFormInstance ? (
                    <Fields
                        fields={datasetFields[firstSection.id]}
                        sectionId={firstSection.id}
                        datasetOnlySection={datasetOnlySection}
                        datasetId={datasetId!}
                        parents={parents}
                        toggleSection={toggleSection}
                        getSectionOpen={getSectionOpen}
                        hiddenFields={hiddenFields}
                        datasetHidden={datasetHidden}
                        documentSpecificHiddenFields={documentSpecificHiddenFields}
                        parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                        parentFieldId={parentFieldId}
                    />
                ) : (datasetSections.map(section => (
                    <Section
                        section={section}
                        key={section.id}
                        formDefinition={formDefinition}
                        datasetId={datasetId!}
                        parents={parents}
                        toggleSection={toggleSection}
                        getSectionOpen={getSectionOpen}
                        hiddenFields={hiddenFields}
                        datasetHidden={datasetHidden}
                        documentSpecificHiddenFields={documentSpecificHiddenFields}
                        parentHiddenDocumentNameIds={parentHiddenDocumentNameIds}
                        parentFieldId={parentFieldId}
                    />
                )))}
            </Scrollable>
        </div>
    );
};
